<template>
  <ProjectFilters
    :team-members="teamMembers"
    @filters-updated="filterUpdated"
    @load-more-owners="handleLoadMoreOwners"
  />
  <div>
    <BaseDataTable
      :entries="entries"
      :loading="$apollo.queries.projects.loading"
      :columns="columns"
      :paginator-info="projects?.paginatorInfo"
      :settings="settings"
      @search="search"
    >
      <template #Status="{ row }">
        <WdsTooltip v-if="row.endDate">
          <WdsLabel subtle error>{{ $t('Ended') }}</WdsLabel>
          <template #tooltip-content>
            {{ $t('Project was ended :date', { date: row.endDate }) }}
          </template>
        </WdsTooltip>

        <WdsLabel v-else subtle success>{{ $t('Active') }}</WdsLabel>
      </template>

      <template #Name="{ row }">
        <div class="tw-max-w-[200px]">
          <a class="tw-font-medium" :href="projectHref(row.id)">
            {{ row.name }}
          </a>
        </div>
      </template>

      <template #JobCount="{ row }">
        <strong class="tw-font-medium tw-pl-5">
          {{ row.jobCount }}
        </strong>
      </template>
    </BaseDataTable>
  </div>
</template>

<script>
import gql from 'graphql-tag'

import { WdsLabel, WdsTooltip } from '@worksome/wds-components'
import { markRaw } from 'vue'
import ProgressBar from '@/components/Projects/ProgressBar.vue'
import ProjectActions from '@/components/Projects/ProjectActions.vue'
import ProjectFilters from '@/components/Projects/ProjectFilters.vue'
import BaseDataTable from '@/components/BaseDataTable'
import { TdOwners } from '@/components/BaseDataTable/Cells'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { textHelpers } from '@/utils/tools'

export default {
  components: {
    BaseDataTable,
    // eslint-disable-next-line vue/no-unused-components -- Baseline eslint ignore please fix if working on this
    ProgressBar,
    WdsLabel,
    WdsTooltip,
    ProjectFilters,
  },

  data() {
    const self = this
    return {
      paginationSettings: {
        page: 1,
        displayPerPage: 50,
      },
      searchValue: '',
      columns: [
        {
          key: 'name',
          title: $t('Name'),
          slot: 'Name',
          class: 'vmiddle',
        },
        {
          key: 'description',
          title: $t('Description'),
          class: 'vmiddle tw-max-w-[200px]',
          orderAble: true,
        },
        {
          key: 'owners',
          title: $t('Owner(s)'),
          renderUsing: {
            component: markRaw(TdOwners),
            componentProps: (project) => ({
              owners: project.owners,
            }),
          },
          class: 'vmiddle',
        },
        {
          key: 'jobCount',
          title: $t('#Jobs'),
          slot: 'JobCount',
          class: 'vmiddle',
          orderAble: false,
        },
        {
          key: 'internalBudget',
          title: $t('Budget'),
          colClass: { 'tw--z-50': true },
          renderUsing: {
            component: markRaw(ProgressBar),
            componentProps(project) {
              return {
                currency: project.currency,
                budget: project.internalBudget,
                allocatedBudget: project.allocatedBudget,
                spent: project.spent,
              }
            },
          },
          class: 'vmiddle tw-w-64 tw-min-w-full',
          orderAble: false,
        },
        {
          key: 'status',
          title: $t('Status'),
          slot: 'Status',
          class: 'vmiddle',
        },
        {
          key: 'actions',
          title: $t('Actions'),
          renderUsing: {
            component: markRaw(ProjectActions),
            componentProps(project) {
              return {
                project,
                inline: true,
                onDeleted: self.refresh,
                onSubmitted(submitStatus) {
                  if (submitStatus === 'open') {
                    self.refresh()
                  }
                },
              }
            },
          },
        },
      ],
      filter: {},
      teamMembers: [],
      teamMembersPage: 1,
      teamMembersPaginationInfo: {},
    }
  },

  mounted() {
    this.teamMembers.push({
      id: window?.currentAccountOwner?.globalId,
      name: window?.currentAccountOwner?.name,
    })
  },

  apollo: {
    $client: 'publicApi',
    projects: {
      query: gql`
        query Projects(
          $accounts: [ID!]
          $page: Int!
          $count: Int!
          $search: String
          $owners: [ID!]
          $status: [ProjectStatusFilter!]
        ) {
          projects(
            accounts: $accounts
            first: $count
            page: $page
            search: $search
            owners: $owners
            status: $status
          ) {
            paginatorInfo {
              ...PaginatorInfoFragment
              count
              currentPage
              total
            }
            data {
              id
              name
              description
              currency
              jobs {
                paginatorInfo {
                  total
                }
              }
              creator {
                ... on User {
                  id
                  name
                }
              }
              owners {
                ...OwnerFragment
              }
              internalBudget
              allocatedBudget
              spent
              endDate
            }
          }
        }
        ${TdOwners.fragments.ownerFragment}
        ${BaseDataTable.fragments.pagination}
      `,
      debounce: 250,
      variables() {
        return {
          accounts: [this.account.globalId],
          page: this.paginationSettings.page,
          count: this.paginationSettings.displayPerPage,
          search: this.searchValue,
          owners: this.filter.owners?.filter((o) => o) ?? [],
          status: this.filter.status ?? [],
        }
      },
    },

    accounts: {
      query: gql`
        query accounts($page: Int!) {
          accounts {
            id
            ... on Company {
              teamMembers(page: $page) {
                data {
                  id
                  name
                }
                paginatorInfo {
                  count
                  currentPage
                  lastPage
                  hasMorePages
                  total
                }
              }
            }
          }
        }
      `,
      variables() {
        return {
          page: this.teamMembersPage,
        }
      },
      update(result) {
        const company = result.accounts
          .filter(
            (account) => account.__typename === 'Company' && account.id === this.account.globalId,
          )
          .pop()

        if (company) {
          this.teamMembers = this.teamMembers.concat(company.teamMembers.data)
          this.teamMembersPaginationInfo = company.teamMembers.paginatorInfo
        }

        return result
      },
    },
  },

  computed: {
    settings() {
      return {
        pageLength: this.paginationSettings.displayPerPage, // Elements per page
        search: { search: this.searchValue }, // Current search
        displayStart: (this.paginationSettings.page - 1) * this.paginationSettings.displayPerPage, // Page number
        autoWidth: false, // Stops resizing issues from occurring
      }
    },
    entries() {
      return (this.projects?.data || []).map((result) => ({
        ...result,
        internalBudget: result.internalBudget === null ? 0 : result.internalBudget,
        allocatedBudget: result.allocatedBudget === null ? 0 : result.allocatedBudget,
        creator: {
          ...result.creator,
          initials: textHelpers.nameStringToInitials(result.creator.name),
          hasPicture: false,
          picture: null,
        },
        jobCount: result.jobs.paginatorInfo.total,
      }))
    },
  },

  methods: {
    refresh() {
      this.$apollo.queries.projects.refetch()
    },

    projectHref(id) {
      return laravelNamedRoute(namedRoutes.projects.show, { project: id })
    },
    search(options) {
      this.paginationSettings.displayPerPage = options.perPage
      this.paginationSettings.page = options.page + 1
      this.searchValue = options.search
    },

    filterUpdated($event) {
      this.filter = $event
      this.paginationSettings.page = 1
    },

    handleLoadMoreOwners() {
      if (this.teamMembersPaginationInfo?.hasMorePages) {
        this.teamMembersPage = this.teamMembersPaginationInfo.currentPage + 1
      }
    },
  },
}
</script>

<style scoped>
:deep(th) {
  z-index: -1;
}
</style>
